import { useCallback, useEffect, useRef } from 'react';
import {
  useQuizResultActions,
  useQuizGameState,
  useQuizMetaContext,
  useQuizResults,
} from '@mentimeter/quiz';
import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import { GameStateEnums, type InitialGameStates } from '@mentimeter/quiz-types';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { GameStatesSwitcher } from './GameStateSwitcher';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type QuizSlideState = {
  started?: boolean;
  starting?: boolean;
};

export const PresentersPresentationDataHandler = ({
  initialGameState,
  activeSession,
}: {
  initialGameState: InitialGameStates;
  activeSession: number;
}) => {
  const { useQuestion, useSlideState, useConfig } = usePresentationContext();
  const { readonly } = useConfig();
  const {
    countdown_to_vote: countdownToVote,
    image,
    time_based_scoring,
    question,
    question_styled,
    title_meta,
    question_description,
    id,
    layout,
    choices,
  } = useQuestion();
  const { totalCount, currentIndex } = useQuizMetaContext();
  const { data = [], isPreviewResults } =
    useQuizResults<QuizPlayersScoreData>();
  const { markAnswer } = useQuizResultActions(id);
  const {
    gameState,
    timeOffset = 0,
    connectedPlayers,
    respondents,
    startQuiz: _startQuiz,
    createQuiz,
    everyoneHasVoted,
    connected,
    connectedWithDelay,
  } = useQuizGameState();
  const [{ started, starting }, setSlideState] =
    useSlideState<QuizSlideState>();

  const startQuiz = useCallback(() => {
    _startQuiz({ countdownToVote });
    setSlideState({ started: true, starting: false });
  }, [countdownToVote, setSlideState, _startQuiz]);

  /**
   * We need to reset our slide state whenever a presenter shows a quiz slide to be able
   * to restart a quiz for example.
   */
  useEffect(() => {
    setSlideState({ started: false, starting: false });
  }, [setSlideState]);

  useEffect(() => {
    if (starting && !started) {
      startQuiz();
    }
  }, [startQuiz, started, starting]);

  const prevActiveSession = useRef(activeSession);
  const createdRef = useRef(false);
  useEffect(() => {
    const sessionChanged = prevActiveSession.current !== activeSession;
    if ((!gameState && !createdRef.current) || sessionChanged) {
      createQuiz(sessionChanged ? GameStateEnums.LOBBY : initialGameState);
      createdRef.current = true;
      prevActiveSession.current = activeSession;
    }
  }, [createQuiz, initialGameState, activeSession, gameState]);

  const onReload = useCallback(() => {
    if (connected) {
      createQuiz(initialGameState);
    } else {
      window.location.reload();
    }
  }, [connected, createQuiz, initialGameState]);

  const correctAnswer = (choices[0] || {}).label;

  return (
    <GameStatesSwitcher
      correctAnswer={correctAnswer}
      imageUrl={image?.presets?.original?.url ?? image?.presets?.large?.url}
      timeBasedScoring={time_based_scoring}
      question={question}
      questionStyled={question_styled}
      meta={title_meta}
      description={question_description}
      layout={layout}
      isReadonly={readonly}
      onMarkAnswer={markAnswer}
      results={data}
      isPreviewResults={Boolean(isPreviewResults)}
      gameState={gameState}
      timeOffset={timeOffset}
      connectedPlayers={connectedPlayers}
      respondents={respondents}
      onReload={onReload}
      everyoneHasVoted={everyoneHasVoted}
      connectedWithDelay={connectedWithDelay}
      totalQuizQuestions={totalCount}
      currentQuizQuestionIndex={currentIndex}
    />
  );
};
